import { Injectable } from '@angular/core';
import { BleClient, numbersToDataView } from '@capacitor-community/bluetooth-le';
import { Capacitor } from '@capacitor/core';
import type { Observable } from 'rxjs';
import { catchError, forkJoin, from, map, of, switchMap, tap, throwError, timer } from 'rxjs';

import type { BleDeviceInfo, BleServiceResponse, DeviceData } from '../../../interfaces/ble.interface';
import { DeviceType, DTI_6_BLE_CONFIG } from '../../../interfaces/ble.interface';

import { BaseDeviceAdapter } from './base-device.adapter';

/**
 * Adapter for ZEISS DTI_6 devices using direct BLE characteristics
 */
@Injectable({
  providedIn: 'root',
})
export class Dti6DeviceAdapter extends BaseDeviceAdapter {
  readonly deviceType: DeviceType = DeviceType.DTI_6;
  readonly deviceNamePrefix: string = DTI_6_BLE_CONFIG.DEVICE_NAME_PREFIX;
  readonly identifyingService: string = DTI_6_BLE_CONFIG.IDENTIFYING_SERVICE;

  private readonly CONNECT_TIMEOUT_MS = 30_000;
  private readonly RECONNECT_DELAY_MS = 1_000;

  connect$(deviceId: string, onDisconnect: (deviceId: string) => void): Observable<BleServiceResponse<BleDeviceInfo>> {
    const state = { isReady: false, bondingDisconnect: false };

    const onBleDisconnect = (disconnectedDeviceId: string): void => {
      if (!state.isReady) {
        state.bondingDisconnect = true;
        return;
      }
      onDisconnect(disconnectedDeviceId);
    };

    return from(BleClient.disconnect(deviceId).catch(() => {})).pipe(
      switchMap(() => this.connectAndDiscover$(deviceId, onBleDisconnect)),
      // Platform-agnostic: if a pairing-induced disconnect arrived mid-setup on either OS,
      // reconnect once. iOS drops the link during the passcode dialog on some devices and
      // Android can do the same when the CCCD attempts to subscribe before bonding.
      switchMap(() => this.reconnectIfBondingDropped$(deviceId, state, onBleDisconnect)),
      switchMap(() => this.androidEnsureBonded$(deviceId)),
      // Another reconnect slot: on Android, createBond can cause a brief drop after bonding.
      switchMap(() => this.reconnectIfBondingDropped$(deviceId, state, onBleDisconnect)),
      tap(() => {
        state.isReady = true;
      }),
      map(() => ({
        success: true,
        data: {
          deviceId,
          name: 'DTI_6 Device',
          isConnected: true,
        } as BleDeviceInfo,
      })),
      catchError((error: unknown) =>
        from(BleClient.disconnect(deviceId).catch(() => undefined)).pipe(
          switchMap(() => {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            return throwError(() => ({
              success: false,
              error: `Connection failed: ${errorMessage}`,
            }));
          })
        )
      )
    );
  }

  private connectAndDiscover$(deviceId: string, onBleDisconnect: (id: string) => void): Observable<void> {
    return from(BleClient.connect(deviceId, onBleDisconnect, { timeout: this.CONNECT_TIMEOUT_MS })).pipe(
      switchMap(() => this.discoverAndValidateServices$(deviceId))
    );
  }

  private reconnectIfBondingDropped$(deviceId: string, state: { bondingDisconnect: boolean }, onBleDisconnect: (id: string) => void): Observable<void> {
    if (!state.bondingDisconnect) return of(void 0);
    state.bondingDisconnect = false;
    return timer(this.RECONNECT_DELAY_MS).pipe(switchMap(() => this.connectAndDiscover$(deviceId, onBleDisconnect)));
  }

  private androidEnsureBonded$(deviceId: string): Observable<void> {
    if (Capacitor.getPlatform() !== 'android') return of(void 0);
    return from(BleClient.isBonded(deviceId)).pipe(
      switchMap(bonded => {
        if (bonded) return of(void 0);
        return from(BleClient.createBond(deviceId)).pipe(catchError(() => of(void 0)));
      })
    );
  }

  disconnect$(deviceId: string): Observable<BleServiceResponse<void>> {
    return from(BleClient.disconnect(deviceId)).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return of({
          success: false,
          error: `Disconnect failed: ${errorMessage}`,
        });
      })
    );
  }

  readBatteryLevel$(deviceId: string): Observable<BleServiceResponse<number>> {
    return from(BleClient.read(deviceId, DTI_6_BLE_CONFIG.SERVICES['DEVICE_ACCESSORY_INFO'], DTI_6_BLE_CONFIG.CHARACTERISTICS['BATTERY_LEVEL'])).pipe(
      map(result => ({ success: true, data: result.getUint8(0) })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Battery read failed: ${errorMessage}`,
        }));
      })
    );
  }

  readBrightnessLevel$(deviceId: string): Observable<BleServiceResponse<number>> {
    return from(BleClient.read(deviceId, DTI_6_BLE_CONFIG.SERVICES['DISPLAY_SETTINGS'], DTI_6_BLE_CONFIG.CHARACTERISTICS['BRIGHTNESS_LEVEL'])).pipe(
      map(result => ({ success: true, data: result.getUint8(0) })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Brightness read failed: ${errorMessage}`,
        }));
      })
    );
  }

  writeBrightnessLevel$(deviceId: string, level: number): Observable<BleServiceResponse<void>> {
    if (level < 0 || level > 8 || !Number.isInteger(level)) {
      return throwError(() => ({
        success: false,
        error: 'Brightness level must be an integer between 0 and 8',
      }));
    }

    return from(
      BleClient.write(deviceId, DTI_6_BLE_CONFIG.SERVICES['DISPLAY_SETTINGS'], DTI_6_BLE_CONFIG.CHARACTERISTICS['BRIGHTNESS_LEVEL'], numbersToDataView([level]))
    ).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Brightness write failed: ${errorMessage}`,
        }));
      })
    );
  }

  loadInitialData$(deviceId: string, updateData: (data: Partial<DeviceData>) => void): Observable<void> {
    return this.readBatteryLevel$(deviceId).pipe(
      tap(result => {
        if (result.success && result.data !== undefined) updateData({ batteryLevel: result.data });
      }),
      switchMap(() => this.readBrightnessLevel$(deviceId)),
      tap(result => {
        if (result.success && result.data !== undefined) updateData({ brightnessLevel: result.data });
      }),
      map(() => void 0)
    );
  }

  readDeviceInfo$(deviceId: string): Observable<BleServiceResponse<Partial<DeviceData>>> {
    const readManufacturer = from(BleClient.read(deviceId, 'E18F0600-5E20-4401-B4BC-BDCEDC75A0C3', '00002A29-0000-1000-8000-00805F9B34FB')).pipe(
      map(result => new TextDecoder().decode(result.buffer)),
      catchError(() => of('Unknown'))
    );

    const readModel = from(BleClient.read(deviceId, 'E18F0600-5E20-4401-B4BC-BDCEDC75A0C3', '00002A24-0000-1000-8000-00805F9B34FB')).pipe(
      map(result => new TextDecoder().decode(result.buffer)),
      catchError(() => of('Unknown'))
    );

    const readSerial = from(BleClient.read(deviceId, 'E18F0600-5E20-4401-B4BC-BDCEDC75A0C3', '00002A25-0000-1000-8000-00805F9B34FB')).pipe(
      map(result => new TextDecoder().decode(result.buffer)),
      catchError(() => of('Unknown'))
    );

    return forkJoin({
      manufacturer: readManufacturer,
      modelNumber: readModel,
      serialNumber: readSerial,
    }).pipe(
      map(deviceInfo => ({ success: true, data: deviceInfo })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Device info read failed: ${errorMessage}`,
        }));
      })
    );
  }
}
